import "./dashboard.css";
// Dashboard — painel de comando do gestor. A pergunta que ele responde nos
// primeiros segundos é "a operação está rodando AGORA?", não "quantas ligações
// deram no mês". Por isso a tela é lida de cima para baixo em três camadas:
//
//   1. Agora    — equipe, chamadas ao vivo e fila. Sem período, recarrega sozinho.
//   2. Hoje     — ritmo por hora contra o mesmo horário de ontem.
//   3. Período  — o retrospecto, aí sim com o seletor de datas.
//
// O recorte é a operação INTEIRA (discador + click-to-call). Quem quer só o
// discador tem o Relatório geral, que corta por is_predictive.
//
// Os blocos que dependem de dado da organização toda (equipe, fila) só existem
// para gestor: as rotas por trás deles são requireManager no back, e pedi-las
// como vendedor daria 403. A conta é pura, mora em dashboard-model.ts.
import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./i18n.js";
import { Link } from "@tanstack/react-router";
import type { DateRange } from "react-day-picker";
import { subDays } from "date-fns";
import {
  Activity,
  ArrowRight,
  CheckCircle2,
  Gauge,
  ListFilter,
  Phone,
  PhoneOff,
  Radio,
  Target,
  Timer,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import { type CallStatus } from "./api.js";
import {
  useActiveCalls,
  useDashboardCallsSummary,
  useDashboardConversionsSummary,
  usePaceCallsSummary,
} from "@/hooks/use-calls";
import { useDashboardAgentAvailability, useDashboardQueueTotals } from "@/hooks/use-dialer";
import { useLeadStatuses } from "@/hooks/use-leads";
import { useQueueConfig } from "@/hooks/use-queue-config";
import { StatusBadge } from "./calls-common.js";
import { formatDuration } from "./sip/softphone-status.js";
import { fillPerDay } from "./lib/call-metrics.js";
import {
  activeQueueLabel,
  greetingKey,
  idleAgents,
  periodResult,
  queueColumns,
  queueShares,
  teamPresence,
  todayPace,
} from "./dashboard-model.js";
import {
  DashboardGreeting,
  MetricValue,
  HeroCard,
  HeroKpi,
  HeroTrend,
  KpiTile,
  REVEAL,
  revealDelay,
  SectionCard,
} from "./dashboard-cards.js";
import { IntroProvider, introStep, useIntroPlayback } from "./dashboard-intro.js";
import { PeriodPills } from "./period-pills.js";
import { useSession } from "./lib/auth-client.js";
import { isManager } from "@/lib/access";
import { useAccessSnapshot } from "@/hooks/use-viewer-role";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { PaceIllustration, StatusBreakdownIllustration } from "@/components/empty-illustrations";
import { cn } from "@/lib/utils";
import { useDateRangeSearch } from "@/lib/date-range-search";
import { last7DaysRange } from "@/lib/report-period";

// Gráficos (recharts) carregados sob demanda: o recharts (~102 KB gzip) fica
// fora do bundle inicial, então a camada "Agora" pinta primeiro e os gráficos
// entram logo em seguida. O fallback preserva a altura para não haver salto.
const CallsPerDayChart = lazy(() =>
  import("./dashboard-charts.js").then((m) => ({
    default: m.CallsPerDayChart,
  })),
);
const AvgDurationChart = lazy(() =>
  import("./dashboard-charts.js").then((m) => ({
    default: m.AvgDurationChart,
  })),
);
const TodayPaceChart = lazy(() =>
  import("./dashboard-charts.js").then((m) => ({ default: m.TodayPaceChart })),
);
const TodayPaceSparkline = lazy(() =>
  import("./dashboard-charts.js").then((m) => ({
    default: m.TodayPaceSparkline,
  })),
);

function ChartFallback({ height = 220 }: { height?: number }) {
  return <Skeleton style={{ height }} className="w-full" />;
}

// Consulta ao vivo que falhou. Sem isto o cartão caía no vazio ("tudo
// tranquilo", "fila vazia") — um falso negativo justo na camada que o gestor
// lê para saber se a operação está rodando.
function LiveError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      className="w-full rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"
    >
      <p>{t("dashboard.live.loadError")}</p>
      <Button type="button" variant="outline" size="sm" className="mt-3" onClick={onRetry}>
        {t("dashboard.live.retry")}
      </Button>
    </div>
  );
}

// De quanto em quanto tempo a camada "Agora" se atualiza. 10s é o mesmo ritmo
// da tela de Chamadas em andamento — o gestor vê os dois lado a lado.
const LIVE_REFETCH_MS = 10_000;
// Fila e ritmo mudam devagar; recarregar a cada 10s só gastaria consulta.
const SLOW_REFETCH_MS = 60_000;
// Quantos status da fila cabem no cartão antes de virar "+ N em outros".
const QUEUE_COLUMNS = 4;
// Quantos parados aparecem nominalmente. Mais que isso vira lista, não alarme.
const IDLE_NAMES = 3;

// Ordem de exibição do resumo por status (previsível, independe do back).
const STATUS_ORDER: CallStatus[] = ["COMPLETED", "IN_PROGRESS", "RINGING", "NO_ANSWER", "FAILED"];

// Início do dia (from) e fim do dia (to) em ISO, no padrão do repo.
function toIso(range: DateRange): { from: string; to: string } {
  const from = new Date(range.from!);
  from.setHours(0, 0, 0, 0);
  const to = new Date(range.to ?? range.from!);
  to.setHours(23, 59, 59, 999);
  return { from: from.toISOString(), to: to.toISOString() };
}

// Ontem 00:00 até agora — a janela do "ritmo de hoje" precisa dos dois dias na
// mesma resposta para comparar hora a hora.
function paceWindow(now: Date): { from: string; to: string } {
  const from = subDays(now, 1);
  from.setHours(0, 0, 0, 0);
  return { from: from.toISOString(), to: now.toISOString() };
}

// "1:02:03" para o tempo parado do vendedor: a mesma formatação de duração do
// resto do app.
function idleLabel(seconds: number): string {
  return formatDuration(seconds);
}

export function Dashboard() {
  const { t } = useTranslation();
  // Abertura só na primeira entrada desta carga do app (logo após o login);
  // nas voltas ao Painel, a entrada discreta de sempre.
  const intro = useIntroPlayback();
  const reveal = intro ? "dashboard-intro" : REVEAL;
  const step = intro ? introStep : revealDelay;
  // Período do retrospecto: últimos 7 dias (hoje inclusive). Vale só para a
  // terceira camada — as duas de cima são sempre "agora" e "hoje".
  // Período na URL (?from=&to=): mexer no calendário reescreve o search param,
  // então recarregar ou compartilhar o link refaz a busca no mesmo período.
  // Padrão sem nada na URL: os últimos 7 dias (ver lib/report-period).
  const { range, setRange } = useDateRangeSearch(last7DaysRange);

  // Relógio da tela. O tempo parado do vendedor e a hora atual do ritmo mudam
  // sozinhos; sem este tique a tela envelheceria em silêncio entre refetches.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), LIVE_REFETCH_MS);
    return () => clearInterval(id);
  }, []);

  // Papel-base só para o gate dos blocos de organização (equipe/fila) — vem do
  // cache do bootstrap (/v1/access/me), sem precisar da lista de membros.
  const { data: session } = useSession();
  const { baseRole } = useAccessSnapshot();
  const canSeeOrg = isManager(baseRole);

  const hasRange = !!range?.from;
  const iso = useMemo(() => (hasRange ? toIso(range!) : null), [hasRange, range]);

  // --- Camada 1: agora ---
  const activeQuery = useActiveCalls({ refetchInterval: LIVE_REFETCH_MS });
  const agentsQuery = useDashboardAgentAvailability({
    enabled: canSeeOrg,
    refetchInterval: LIVE_REFETCH_MS,
  });
  const queueQuery = useDashboardQueueTotals({
    enabled: canSeeOrg,
    refetchInterval: SLOW_REFETCH_MS,
  });
  const statusesQuery = useLeadStatuses({ enabled: canSeeOrg });
  // Só para dizer qual fila nomeada está no ar. A rota é de gestor, e o cartão
  // que usa isto já vive atrás do mesmo gate.
  const queueConfigQuery = useQueueConfig({ enabled: canSeeOrg });

  // --- Camada 2: hoje ---
  const paceIso = useMemo(() => paceWindow(now), [now]);
  const paceQuery = usePaceCallsSummary(paceIso.from.slice(0, 13), () => paceWindow(new Date()), {
    refetchInterval: SLOW_REFETCH_MS,
  });

  // --- Camada 3: período ---
  const summaryQuery = useDashboardCallsSummary(iso, { enabled: hasRange });
  const conversionsQuery = useDashboardConversionsSummary(iso, { enabled: hasRange });

  const activeCount = activeQuery.data?.calls.length ?? 0;
  const agents = agentsQuery.data ?? [];
  const idle = useMemo(() => idleAgents(agents, now), [agents, now]);
  const presence = useMemo(() => teamPresence(agents), [agents]);

  // Primeiro nome só: o cabeçalho é uma saudação, não um crachá.
  const firstName = session?.user.name?.trim().split(/\s+/)[0] ?? "";

  const queue = useMemo(
    () => queueColumns(queueQuery.data?.totals ?? [], statusesQuery.data ?? [], QUEUE_COLUMNS),
    [queueQuery.data, statusesQuery.data],
  );

  const pace = useMemo(() => todayPace(paceQuery.data?.perHour ?? [], now), [paceQuery.data, now]);
  const todayAnswered = pace.answeredToday;
  const shares = useMemo(() => queueShares(queue), [queue]);
  const activeQueue = activeQueueLabel(queueConfigQuery.data);

  const summary = summaryQuery.data;
  const period = summary ? periodResult(summary) : null;
  const total = period?.total ?? 0;
  const completed = period?.answered ?? 0;
  const rate = period?.ratePct != null ? `${period.ratePct}%` : "—";
  const avgSec = period?.avgSec ?? null;
  const conversionRate = conversionsQuery.data?.totals.rate ?? null;
  const conversions = conversionsQuery.data?.totals.conversions ?? 0;

  const statusMap = new Map(summary?.byStatus.map((s) => [s.status, s.count]));
  const series = useMemo(
    () =>
      range?.from ? fillPerDay(summary?.perDay ?? [], range.from, range.to ?? range.from) : [],
    [summary, range],
  );
  const hasAvgSeries = series.some((d) => d.avgSec != null);

  return (
    <IntroProvider value={intro}>
      <main className="w-full min-w-0 p-4 sm:p-6" data-intro={intro || undefined}>
        <DashboardGreeting
          greeting={t(`dashboard.greeting.${greetingKey(now)}`)}
          firstName={firstName}
          now={now}
          agents={agents}
          presence={presence}
          className={reveal}
          style={step(0)}
        />

        {/* --- Hoje: o herói e o que está acontecendo agora ------------------- */}
        <div className="mt-6 grid gap-4 lg:grid-cols-3">
          <HeroCard
            eyebrow={t("dashboard.pace.title")}
            // Consulta que falhou mostra "—", não um "0" que diria "nada hoje".
            value={paceQuery.error ? "—" : String(pace.today)}
            unit={t(
              pace.today === 1
                ? "dashboard.pace.callsToday_one"
                : "dashboard.pace.callsToday_other",
            )}
            trend={
              pace.deltaPct != null ? (
                <HeroTrend icon={pace.deltaPct >= 0 ? TrendingUp : TrendingDown}>
                  {pace.deltaPct > 0 ? "+" : ""}
                  {t("dashboard.pace.vsYesterday", {
                    percent: pace.deltaPct,
                    count: pace.yesterdayToNow,
                  })}
                </HeroTrend>
              ) : undefined
            }
            sparkline={
              pace.today > 0 ? (
                <Suspense fallback={null}>
                  <TodayPaceSparkline data={pace.series} />
                </Suspense>
              ) : undefined
            }
            className={cn("dashboard-hero lg:col-span-2", reveal)}
            style={step(1)}
          >
            <div className="mt-6 flex flex-wrap divide-x divide-[color-mix(in_oklab,var(--hero-ink)_16%,transparent)]">
              <HeroKpi
                icon={CheckCircle2}
                value={paceQuery.error ? "—" : String(todayAnswered)}
                label={t("dashboard.metrics.answered")}
                hint={t("dashboard.today.answeredHint")}
              />
              <HeroKpi
                icon={Radio}
                value={activeQuery.error ? "—" : String(activeCount)}
                label={t("dashboard.live.calls")}
                hint={t("dashboard.today.liveHint")}
              />
              {canSeeOrg && (
                <HeroKpi
                  icon={Users}
                  value={agentsQuery.error ? "—" : `${presence.inCall}/${presence.total}`}
                  label={t("dashboard.today.onAir")}
                  hint={t("dashboard.today.onAirHint", { count: presence.available })}
                />
              )}
            </div>
          </HeroCard>

          {/* Espelho do "Agora" do Connect: um estado, não uma métrica. O texto
            muda de tom conforme a operação esteja rodando, parada ou vazia. */}
          <SectionCard
            icon={Activity}
            title={t("dashboard.now.title")}
            to="/monitoring/active-calls"
            linkLabel={t("dashboard.live.open")}
            className={cn("flex flex-col", reveal)}
            contentClassName="flex-1"
            style={step(2)}
          >
            {/* Estica até o fim do cartão e empurra o alerta para o rodapé: sem
              isso o bloco fica encolhido no topo com um vazio enorme embaixo,
              porque o irmão da esquerda (o herói) é bem mais alto. */}
            {activeQuery.error ? (
              <LiveError onRetry={() => void activeQuery.refetch()} />
            ) : (
              <div className="flex h-full flex-col items-start justify-between gap-3">
                <span
                  className={cn(
                    "flex size-12 items-center justify-center rounded-2xl",
                    activeCount > 0
                      ? "bg-primary/12 text-primary"
                      : "bg-emerald-500/12 text-emerald-600 dark:text-emerald-400",
                  )}
                >
                  {activeCount > 0 ? (
                    <Radio aria-hidden="true" className="size-6" />
                  ) : (
                    <CheckCircle2 aria-hidden="true" className="size-6" />
                  )}
                </span>
                <div>
                  <p className="text-lg font-semibold tracking-tight">
                    {activeCount > 0
                      ? t("dashboard.now.running", { count: activeCount })
                      : t("dashboard.now.quiet")}
                  </p>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    {activeCount > 0
                      ? t("dashboard.live.autoRefresh")
                      : t("dashboard.now.quietHint")}
                  </p>
                </div>
                {canSeeOrg && idle.length > 0 && (
                  <Link
                    to="/monitoring/active-calls"
                    className="mt-1 flex items-center gap-1.5 text-sm text-amber-700 hover:underline dark:text-amber-500"
                  >
                    {t("dashboard.now.idleAlert", { count: idle.length })}
                    <ArrowRight aria-hidden="true" className="size-3.5" />
                  </Link>
                )}
              </div>
            )}
          </SectionCard>
        </div>

        {/* --- Agora: fila e equipe ------------------------------------------ */}
        {canSeeOrg && (
          <div className="mt-4 grid gap-4 lg:grid-cols-3">
            <SectionCard
              icon={ListFilter}
              title={t("dashboard.live.queue")}
              description={t("dashboard.queue.description")}
              to="/management/queue-kanban"
              linkLabel={t("dashboard.live.viewBoard")}
              className={cn("lg:col-span-2", reveal)}
              style={step(3)}
            >
              {/* Qual fila nomeada o discador está entregando. Só aparece quando
                existe fila cadastrada — sem nenhuma, o recurso não está em uso
                e o aviso seria ruído. */}
              {activeQueue && (
                <div className="mb-4">
                  {activeQueue.kind === "named" ? (
                    <Badge variant="outline" className="gap-1.5">
                      <span aria-hidden="true" className="size-1.5 rounded-full bg-emerald-500" />
                      {t("dashboard.queue.active", { name: activeQueue.name })}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="gap-1.5 text-amber-700 dark:text-amber-500">
                      <span aria-hidden="true" className="size-1.5 rounded-full bg-current" />
                      {t("dashboard.queue.noneActive")}
                    </Badge>
                  )}
                </div>
              )}
              {queueQuery.isLoading || statusesQuery.isLoading ? (
                <Skeleton className="h-28 w-full" />
              ) : queueQuery.error || statusesQuery.error ? (
                <LiveError
                  onRetry={() => {
                    if (queueQuery.error) void queueQuery.refetch();
                    if (statusesQuery.error) void statusesQuery.refetch();
                  }}
                />
              ) : queue.total === 0 ? (
                <div className="text-sm text-muted-foreground">
                  <p>{t("dashboard.live.emptyQueue")}</p>
                  <p className="mt-1">
                    {t("dashboard.live.unmappedBefore")}{" "}
                    <Link to="/management/queue-config" className="underline hover:text-foreground">
                      {t("dashboard.live.checkStatusMap")}
                    </Link>
                    .
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-semibold tabular-nums tracking-tight">
                      <MetricValue value={queue.total} />
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {t("dashboard.live.waitingLeads", { count: queue.total })}
                    </span>
                  </div>
                  <ul className="mt-4 space-y-2.5">
                    {shares.map((column, index) => (
                      <li
                        key={column.key}
                        className="dashboard-data-row flex items-center gap-3 rounded-sm"
                        style={{ ["--i" as string]: index }}
                      >
                        <span className="flex w-36 min-w-0 shrink-0 items-center gap-2">
                          <span
                            aria-hidden="true"
                            className="size-2 shrink-0 rounded-full"
                            style={{ backgroundColor: column.color }}
                          />
                          <span className="truncate text-sm">{column.label}</span>
                        </span>
                        <span className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                          <span
                            className="dashboard-status-bar block h-full rounded-full"
                            style={{
                              width: `${column.share}%`,
                              backgroundColor: column.color,
                            }}
                          />
                        </span>
                        <span className="w-20 shrink-0 text-right text-sm tabular-nums text-muted-foreground">
                          {column.total} · {column.share}%
                        </span>
                      </li>
                    ))}
                  </ul>
                  {queue.rest > 0 && (
                    <p className="mt-3 text-xs text-muted-foreground">
                      {t("dashboard.live.otherStatuses", { count: queue.rest })}
                    </p>
                  )}
                </>
              )}
            </SectionCard>

            <SectionCard
              icon={Users}
              title={t("dashboard.live.team")}
              description={t("dashboard.team.description")}
              to="/monitoring/active-calls"
              linkLabel={t("dashboard.live.viewTeam")}
              className={reveal}
              style={step(4)}
            >
              {agentsQuery.isLoading ? (
                <Skeleton className="h-28 w-full" />
              ) : agentsQuery.error ? (
                <LiveError onRetry={() => void agentsQuery.refetch()} />
              ) : agents.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("dashboard.live.noSellers")}</p>
              ) : idle.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("dashboard.live.noIdle")}</p>
              ) : (
                <ul className="space-y-3">
                  {idle.slice(0, IDLE_NAMES).map((agent, index) => (
                    <li
                      key={agent.userId}
                      className="dashboard-data-row flex items-center gap-3 rounded-sm"
                      style={{ ["--i" as string]: index }}
                    >
                      <Avatar size="sm">
                        <AvatarFallback className="bg-amber-500/12 text-[0.7rem] font-semibold text-amber-700 dark:text-amber-500">
                          {agent.name.trim().charAt(0).toUpperCase() || "?"}
                        </AvatarFallback>
                      </Avatar>
                      <span className="min-w-0 flex-1 truncate text-sm">{agent.name}</span>
                      <Badge variant="outline" className="shrink-0 tabular-nums">
                        {t("dashboard.live.idleFor", { duration: idleLabel(agent.seconds) })}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>
          </div>
        )}

        {/* --- Hoje, em detalhe ---------------------------------------------- */}
        <SectionCard
          icon={Gauge}
          title={t("dashboard.charts.paceDetail")}
          description={t("dashboard.charts.paceDetailHint")}
          className={cn("mt-4", reveal)}
          style={step(5)}
        >
          {paceQuery.isLoading ? (
            <ChartFallback height={200} />
          ) : paceQuery.error ? (
            <LiveError onRetry={() => void paceQuery.refetch()} />
          ) : pace.today === 0 && pace.yesterdayToNow === 0 ? (
            <Empty data-testid="dashboard-pace-empty">
              <EmptyHeader>
                <EmptyMedia>
                  <PaceIllustration />
                </EmptyMedia>
                <EmptyTitle>{t("dashboard.pace.empty")}</EmptyTitle>
              </EmptyHeader>
            </Empty>
          ) : (
            <Suspense fallback={<ChartFallback height={200} />}>
              <TodayPaceChart data={pace.series} />
            </Suspense>
          )}
        </SectionCard>

        {/* --- Período ------------------------------------------------------- */}
        <div
          className={cn(
            "mt-8 mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between",
            reveal,
          )}
          style={step(6)}
        >
          <div>
            <h2 className="text-lg font-semibold tracking-tight">{t("dashboard.period.title")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("dashboard.period.description")}
            </p>
          </div>
          <div className="dashboard-period-control rounded-md focus-within:ring-2 focus-within:ring-ring/25 focus-within:ring-offset-2">
            <PeriodPills range={range} onChange={setRange} />
          </div>
        </div>

        {summaryQuery.error && (
          <div className="mb-4 rounded-2xl border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
            {t("dashboard.period.loadError", {
              message: (summaryQuery.error as Error).message,
            })}
          </div>
        )}

        {summaryQuery.isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5" aria-busy="true">
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton key={index} className="h-32 w-full rounded-2xl" />
            ))}
          </div>
        ) : (
          <>
            <div
              className={cn("dashboard-kpi-grid grid gap-4 sm:grid-cols-2 lg:grid-cols-5", reveal)}
              style={step(7)}
            >
              <KpiTile
                icon={Phone}
                label={t("dashboard.metrics.totalCalls")}
                value={String(total)}
                hint={t("dashboard.metrics.selectedPeriod")}
              />
              <KpiTile
                icon={CheckCircle2}
                label={t("dashboard.metrics.answered")}
                value={String(completed)}
                hint={t("dashboard.metrics.answeredDetail")}
                tone="text-emerald-600 dark:text-emerald-400"
                share={total > 0 ? (completed / total) * 100 : null}
              />
              <KpiTile
                icon={PhoneOff}
                label={t("dashboard.metrics.answerRate")}
                value={rate}
                hint={t("dashboard.metrics.answerRateHint")}
                tone="text-primary"
                share={total > 0 ? (completed / total) * 100 : null}
              />
              <KpiTile
                icon={Timer}
                label={t("dashboard.metrics.averageTime")}
                value={avgSec != null ? formatDuration(avgSec) : "—"}
                hint={t("dashboard.metrics.averageTimeHint")}
                tone="text-violet-600 dark:text-violet-400"
              />
              <KpiTile
                icon={Target}
                label={t("dashboard.metrics.successRate")}
                value={conversionRate == null ? "—" : `${conversionRate}%`}
                hint={t("dashboard.metrics.conversions", { count: conversions })}
                tone="text-emerald-600 dark:text-emerald-400"
                share={conversionRate}
              />
            </div>

            <div className={cn("mt-4 grid gap-4 lg:grid-cols-2", reveal)} style={step(8)}>
              <SectionCard icon={Phone} title={t("dashboard.charts.callsPerDay")}>
                {total === 0 ? (
                  <p className="py-10 text-center text-sm text-muted-foreground">
                    {t("dashboard.charts.noCalls")}
                  </p>
                ) : (
                  <Suspense fallback={<ChartFallback />}>
                    <CallsPerDayChart data={series} />
                  </Suspense>
                )}
              </SectionCard>

              <SectionCard icon={Timer} title={t("dashboard.charts.averagePerDay")}>
                {!hasAvgSeries ? (
                  <p className="py-10 text-center text-sm text-muted-foreground">
                    {t("dashboard.charts.noAnswered")}
                  </p>
                ) : (
                  <Suspense fallback={<ChartFallback />}>
                    <AvgDurationChart data={series} />
                  </Suspense>
                )}
              </SectionCard>
            </div>

            {summary && (
              <SectionCard
                icon={ListFilter}
                title={t("dashboard.status.title")}
                className={cn("mt-4", reveal)}
                style={step(9)}
              >
                {total === 0 ? (
                  <Empty data-testid="dashboard-status-empty">
                    <EmptyHeader>
                      <EmptyMedia>
                        <StatusBreakdownIllustration />
                      </EmptyMedia>
                      <EmptyTitle>{t("dashboard.status.empty")}</EmptyTitle>
                    </EmptyHeader>
                  </Empty>
                ) : (
                  <ul className="space-y-3">
                    {STATUS_ORDER.filter((s) => (statusMap.get(s) ?? 0) > 0).map(
                      (status, index) => {
                        const count = statusMap.get(status) ?? 0;
                        const pct = Math.round((count / total) * 100);
                        return (
                          <li
                            key={status}
                            className="dashboard-data-row flex items-center gap-3 rounded-sm"
                            style={{ ["--i" as string]: index }}
                          >
                            <div className="w-32 shrink-0">
                              <StatusBadge status={status} />
                            </div>
                            <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                              <div
                                className="dashboard-status-bar h-full rounded-full bg-primary"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="w-16 shrink-0 text-right text-sm tabular-nums text-muted-foreground">
                              {count} · {pct}%
                            </span>
                          </li>
                        );
                      },
                    )}
                  </ul>
                )}
              </SectionCard>
            )}
          </>
        )}
      </main>
    </IntroProvider>
  );
}
